import { defineStore } from 'pinia'

import { useUserStore } from '@/stores/userStore'

import { STAFF_BASE_PERMISSIONS } from '@/config/permissionsConfig'
import {
	COMPANY_LICENSE_MODULES,
	AVAILABLE_GROUPS_BY_LICENSE_MODULES,
	AVAILABLE_REFERENCE_MATERIALS_BY_LICENSE_MODULES,
} from '@/config/companyLicenseConfig'
import { ROUTER_NAMES } from '@/config/router/routerNamesConfig'

const defaultCompanyStore = {
	companyIsLoaded: false,
	currentCompany: {},
	currentUserCompanyPermission: {
		additional_permissions: {},
		base_permission: '',
	},
	isSearch: false,
}

export const useCompanyStore = defineStore('company', {
	state: () => ({
		companyIsLoaded: defaultCompanyStore.companyIsLoaded,
		currentCompany: defaultCompanyStore.currentCompany,
		currentUserCompanyPermission: defaultCompanyStore.currentUserCompanyPermission,

		isSearch: defaultCompanyStore.isSearch,

		personalCompanySettings: {},
		isLoadingAllPersonalCompanySettings: false,
		loadingPersonalCompanySettings: [],

	}),

	getters: {
		currentUser() {
			const userStore = useUserStore()
			return userStore.currentUser
		},
		currentUserIsCompanyOwner: (state) => state.currentCompany?.owner?.id === state.currentUser.id,

		hasViewEditDeleteManagePermission: (state) =>
			state.currentUserCompanyPermission.base_permission === STAFF_BASE_PERMISSIONS.ALLOW_VIEW_EDIT_DELETE_MANAGE,
		hasViewEditDeletePermission: (state) =>
		state.hasViewEditDeleteManagePermission ||
			state.currentUserCompanyPermission.base_permission === STAFF_BASE_PERMISSIONS.ALLOW_VIEW_EDIT_DELETE,
		hasViewEditPermission: (state) =>
		state.hasViewEditDeletePermission ||
			state.currentUserCompanyPermission.base_permission === STAFF_BASE_PERMISSIONS.ALLOW_VIEW_EDIT,
		hasViewPermission: (state) => state.hasViewEditPermission || state.currentUserCompanyPermission.base_permission === STAFF_BASE_PERMISSIONS.ALLOW_VIEW,

		hasAllowExportPermission: (state) =>
			state.currentUserCompanyPermission.additional_permissions?.allow_export ?? false,

		personalCompanySettingByKey: (state) => (key) => state.personalCompanySettings[key],
		loadingPersonalCompanySettingsStatusByKey: (state) => (key) => state.loadingPersonalCompanySettings.includes(key),

		companyActiveLicense: (state) => (state.currentCompany?.active_license ?? null),
		companyLicenseModules: (state) => (state.companyActiveLicense?.modules_codes ?? []),

		availableRouteGroupsByCompanyLicense: (state) => {
			return [state.companyLicenseModules.map((module) => (AVAILABLE_GROUPS_BY_LICENSE_MODULES[module] ?? []))].flat(2)
		},
		availableRouteReferenceMaterialsByCompanyLicense: (state) => {
			return [state.companyLicenseModules.map((module) => (AVAILABLE_REFERENCE_MATERIALS_BY_LICENSE_MODULES[module] ?? []))].flat(2)
		},

		companyHomePage: (state) => {
			if (state.companyLicenseModules.includes(COMPANY_LICENSE_MODULES.CROP_PRODUCTION)) {
				return ROUTER_NAMES.CROPS_STRUCTURES.DASHBOARD
			}
			return ROUTER_NAMES.PROJECTS.DASHBOARD
		},
	},

	actions: {
		toggleSearch() {
			this.isSearch.value = !this.isSearch.value
		},

		setCurrentCompany(company) {
			const newCompany = Object.assign({}, company)
			/* в некоторых старых компаниях currency с недопустимым значением */
			if (newCompany.currency === 'грн') {
				newCompany.currency = 'UAH'
			} else if (newCompany.currency === '$') {
				newCompany.currency = 'USD'
			} else if (newCompany.currency === 'тг') {
				newCompany.currency = 'KZT'
			}
			// debugger
			// newCompany.active_license.modules_codes = ['base', 'gps_monitoring'/* , 'land_bank' */, 'storage', 'satellite_monitoring', 'logistics'/* , 'crop_production' */]
			this.currentCompany = newCompany
			if (company) {
				this.companyIsLoaded = true
			}
		},

		setCurrentUserCompanyPermission(permission) {
			this.currentUserCompanyPermission = Object.assign({}, permission)
		},

		resetCompanyStore() {
			this.companyIsLoaded = defaultCompanyStore.companyIsLoaded
			this.currentCompany = defaultCompanyStore.currentCompany
			this.currentUserCompanyPermission = defaultCompanyStore.currentUserCompanyPermission

			this.isSearch = defaultCompanyStore.isSearch
		},

		setAllPersonalCompanySettings(settings) {
			return new Promise((resolve) => {
				this.personalCompanySettings = settings
				resolve()
			})
		},

		setPersonalCompanySettings(attr, value) {
			return new Promise((resolve) => {
				this.personalCompanySettings[attr] = value
				resolve()
			})
		},

		setIsLoadingAllPersonalCompanySettingsStatus(status) {
			return new Promise((resolve) => {
				this.isLoadingAllPersonalCompanySettings = status
				resolve()
			})
		},

		setLoadingPersonalCompanySettings(attr, status) {
			return new Promise((resolve) => {
				if (status) {
					this.loadingPersonalCompanySettings.push(attr)
				} else {
					this.loadingPersonalCompanySettings = this.loadingPersonalCompanySettings.filter((item) => item !== attr)
				}
				resolve()
			})
		},

	},
})
